// The panel's checkbox, drawn rather than left to the browser: a hairline box
// that fills with brand cyan and shows a white tick once ticked.
//
// The native input is kept underneath — it carries the role, the label
// association and the keyboard behaviour — and only its paint is replaced, so
// the control still reads as a checkbox to tests and to screen readers.

import React from "react";

export interface CheckboxProps {
  /** Takes a node, not just a string, so a label can carry its own links. */
  label: React.ReactNode;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  /** Second line under the label, for a tick that needs explaining. Its
   *  presence also switches the row from centred to top-aligned. */
  description?: React.ReactNode;
  /** Appended to the label's own classes, so it wins - for rows that need a
   *  different size or colour from the form default. */
  labelClassName?: string;
  /**
   * `"touch"` grows the box to 20px on a phone and leaves it at the form's 16px
   * from sm up, for a tick that is one of a handful on the screen rather than
   * one field among many in a form.
   *
   * A 16px box is the right size inside a form, where it sits in a column of
   * controls and the label beside it is the target anyway. It is the wrong size
   * on a screen whose whole content is three things to tick, where the box is
   * what the eye checks the state of from a foot away.
   */
  size?: "form" | "touch";
  /**
   * Where the box sits against a label of more than one line.
   *
   * `"center"` is the form default and is right there: a field's tick has a
   * short label that does not wrap, and centring is what lines it up with the
   * row either side of it.
   *
   * `"first-line"` is for a label that wraps, where centring puts the box in
   * the gap between two lines of text and the row stops reading as a tick with
   * a sentence beside it. A phone is where this shows - the consent terms are
   * one line each in a 560px column and two or three in a 360px one - but it
   * is not scoped to a phone, because a label long enough to wrap wants the
   * first line at any width, and a label short enough not to renders within a
   * pixel of where centring put it.
   *
   * A row with a `description` is top-aligned whichever is passed: it has a
   * second line by construction.
   */
  align?: "center" | "first-line";
  className?: string;
}

export const Checkbox: React.FC<CheckboxProps> = ({
  label,
  checked,
  onCheckedChange,
  description,
  labelClassName = "",
  size = "form",
  align = "center",
  className = "",
}) => {
  const fieldId = React.useId();
  // The box and the tick are sized together, so the tick keeps its inset.
  const boxSize =
    size === "touch" ? "h-5 w-5 sm:h-4 sm:w-4" : "h-4 w-4";
  const tickSize =
    size === "touch"
      ? "h-[13px] w-[13px] sm:h-[10px] sm:w-[10px]"
      : "h-[10px] w-[10px]";

  // Either reason to leave the centre line puts the box beside the first line
  // of the label, and both want the same nudge under it - so it is the one
  // decision here rather than two that have to agree.
  const toFirstLine = !!description || align === "first-line";

  return (
    <div
      className={`flex gap-2 ${
        toFirstLine ? "items-start" : "items-center"
      } ${className}`}
    >
      <span
        // The 3px is the box set on the label's first line rather than on the
        // top of its line box: the leading over a 15px line is what it pays
        // back, so the box and the cap height of the word beside it start
        // together.
        className={`relative inline-flex flex-shrink-0 items-center justify-center ${boxSize} ${
          toFirstLine ? "mt-[3px]" : ""
        }`}
      >
        <input
          id={fieldId}
          type="checkbox"
          checked={checked}
          onChange={(event) => onCheckedChange(event.target.checked)}
          className={`peer cursor-pointer appearance-none rounded-[2.5px] border-[0.6px] border-[#8B8D98] bg-white transition-colors checked:border-[#00C5D7] checked:bg-[#00C5D7] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 ${boxSize}`}
        />
        {/* A sibling of the input, so the tick can follow `:checked` without
            the component having to re-render to swap an icon. */}
        <svg
          viewBox="0 0 12 10"
          fill="none"
          aria-hidden="true"
          className={`pointer-events-none absolute text-white opacity-0 transition-opacity peer-checked:opacity-100 ${tickSize}`}
        >
          <path
            d="M1 5.1 4.2 8.3 11 1.5"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
      <div>
        <label
          htmlFor={fieldId}
          className={`cursor-pointer select-none text-[13px] 3xl:text-[14px] text-ink-muted ${labelClassName}`}
        >
          {label}
        </label>
        {description && (
          <p className="mt-1 text-[13px] leading-5 text-ink-muted">
            {description}
          </p>
        )}
      </div>
    </div>
  );
};

export default Checkbox;
